import { IS_OE_PERIOD } from 'helpers/datetime'
import dayjs from 'dayjs'
import { createReducer } from 'modules/app-core/redux-utils'
import t from './actionTypes'
import { STORAGE_FILTER } from './constants'

const defaultBenefitEffectiveDate = IS_OE_PERIOD
  ? dayjs().add(1, 'year').startOf('year').format('YYYY-MM-DD')
  : null

const initialFilters = {
  WorkerSubmitted: true,
  HixmeSubmitted: !IS_OE_PERIOD,
  Validated: !IS_OE_PERIOD,
  Other: !IS_OE_PERIOD,
  BenefitEffectiveDate: defaultBenefitEffectiveDate,
  Assignee: [],
}

const initialState = {
  companyId: '',
  keyword: '',
  needsAttention: false,
  needsCancellation: false,
  nonTestUsers: true,
  testUsers: false,
  cartOpen: false,
  sameCarrier: false,
  recordPointer: 10,
  showFilters: false,
  isViewAllSelected: false,
  sortBy: 'name',
  sortDescending: false,
  updateFilter: initialFilters,
  benefitEffectiveDate: defaultBenefitEffectiveDate,
  benefitEffectiveDateForQuery: null,
  benefitEffectiveDateRangeForQuery: null,
  applications: [],
  selectedApplications: [],
  assigneeFilter: [],
  commissionFilters: {
    HasCommissionPartner: true,
    CommissionPartnerSearch: '',
    NoCommissionPartner: true,
  },
  carrierPlanChange: [],
}

export default createReducer(initialState, {
  [t.VIEW_MORE]: (state) => ({
    ...state,
    recordPointer: state.recordPointer + 10,
  }),
  [t.VIEW_ALL]: (state, value) => ({
    ...state,
    isViewAllSelected: value ?? true,
  }),
  [t.LOAD_APPLICATIONS]: (state, applications) => ({
    ...state,
    applications,
  }),
  [t.UPDATE_APPLICATION]: (state, { Id, Status, ...rest }) => {
    const applications = [...state.applications]
    const idx = applications.findIndex((app) => app.Id === Id)
    if (idx === -1) return state
    const updatedApplication = {
      ...applications[idx],
      CarrierApplication: {
        ...applications[idx]?.CarrierApplication,
        ...rest,
        Status,
      },
    }
    applications[idx] = updatedApplication
    return {
      ...state,
      applications,
    }
  },
  [t.UPDATE_KEYWORD]: (state, keyword) => ({
    ...state,
    keyword,
    recordPointer: 10,
  }),
  [t.SET_COMPANY_ID]: (state, companyId) => ({
    ...state,
    companyId,
    keyword: '',
    recordPointer: 10,
  }),
  [t.TOGGLE_FILTERS]: (state) => ({
    ...state,
    showFilters: !state.showFilters,
  }),
  [t.UPDATE_BENEFIT_EFFECTIVE_DATE]: (state, payload) => ({
    ...state,
    benefitEffectiveDate: payload,
  }),
  [t.UPDATE_FILTER]: (state, payload) => {
    window.localStorage.setItem(STORAGE_FILTER, JSON.stringify(payload))
    return {
      ...state,
      updateFilter: payload,
      recordPointer: 10,
      showFilters: false,
    }
  },
  [t.UPDATE_CART_OPEN]: (state, payload) => ({
    ...state,
    cartOpen: payload,
    recordPointer: 10,
    showFilters: false,
  }),
  [t.UPDATE_NEEDS_ATTENTION]: (state, payload) => ({
    ...state,
    needsAttention: payload,
    recordPointer: 10,
    showFilters: false,
  }),
  [t.UPDATE_NEEDS_CANCELLATION]: (state, payload) => ({
    ...state,
    needsCancellation: payload,
    recordPointer: 10,
    showFilters: false,
  }),
  [t.UPDATE_SAME_CARRIER]: (state, payload) => ({
    ...state,
    sameCarrier: payload,
    recordPointer: 10,
    showFilters: false,
  }),
  [t.UPDATE_TEST_USER]: (state, payload) => ({
    ...state,
    ...{
      nonTestUsers: payload.IsNonTestUser,
      testUsers: payload.IsTestUser,
    },
    recordPointer: 10,
    showFilters: false,
  }),
  [t.UPDATE_COMMISSION_FILTER]: (state, payload) => ({
    ...state,
    commissionFilters: payload,
  }),
  [t.UPDATE_ASSIGNEE_FILTER]: (state, payload) => ({
    ...state,
    assigneeFilter: payload,
    recordPointer: 10,
  }),
  [t.UPDATE_SHOW_UNASSIGNED]: (state, payload) => ({
    ...state,
    showUnassigned: payload,
    recordPointer: 10,
  }),
  [t.LOAD_SAVED_FILTERS]: (state, payload) => {
    if (!payload) return state
    return {
      ...state,
      updateFilter: { ...initialFilters, ...payload },
      assigneeFilter: payload.Assignee || [],
    }
  },
  [t.SET_BENEFIT_EFFECTIVE_DATE_FOR_QUERY]: (state, payload) => {
    return {
      ...state,
      benefitEffectiveDateForQuery: payload ?? null,
    }
  },
  [t.SET_BENEFIT_EFFECTIVE_DATE_RANGE_FOR_QUERY]: (state, payload) => {
    return {
      ...state,
      benefitEffectiveDateRangeForQuery: payload ?? null,
    }
  },
  [t.RESET_COMPONENT]: (_) => {
    return { ...initialState }
  },
  [t.RESET_FILTERS]: (state) => {
    window.localStorage.removeItem(STORAGE_FILTER)
    return {
      ...state,
      updateFilter: initialFilters,
      keyword: '',
      cartOpen: false,
      needsAttention: false,
      needsCancellation: false,
      recordPointer: 10,
      showFilters: false,
      isViewAllSelected: false,
      assigneeFilter: [],
      showUnassigned: false,
      benefitEffectiveDate: defaultBenefitEffectiveDate,
      commissionFilters: {
        HasCommissionPartner: true,
        CommissionPartnerSearch: '',
        NoCommissionPartner: true,
      },
      carrierPlanChange: [],
    }
  },
  [t.CHANGE_SORT]: (state, payload) => {
    const sameSortBySelected = payload === state.sortBy
    return {
      ...state,
      sortBy: payload,
      sortDescending: sameSortBySelected ? !state.sortDescending : true,
    }
  },
  [t.SET_SELECTED_APPLICATIONS]: (state, selectedApplications) => {
    return {
      ...state,
      selectedApplications,
    }
  },
  [t.UPDATE_CARRIER_PLAN_CHANGE]: (state, payload) => ({
    ...state,
    carrierPlanChange: payload,
  }),
})
